import React, { Suspense } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { setAppLocales, essentialLocales } from '@fs/zion-locale'
import * as Sentry from '@sentry/browser'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router } from '@fs/zion-router'
import { appPath, sentryDSN, mergeSentryConfig, sentryDefaults as zionSentryDefaults } from '@fs/zion-config'

import { NoticeLoading } from '@fs/zion-icon'
import App from './App'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import './locales'

const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

setAppLocales(essentialLocales)

const treeSentryDefaults = {
  ignoreErrors: [
    /^.*-extension:\/\/.*$/,
    /^.*AbortError: The transaction was aborted.*$/,
    /^.*AbortError: Version change transaction was aborted.*$/,
    /^.*Blocked a frame with origin.*$/,
    /^.*Can't find variable.*$/,
    /^.*Connection is closing.*$/,
    /^.*Connection on cached page closed.*$/,
    /^.*connection was closed.*$/,
    /^.*ChunkLoadError.*$/,
    /^.*Database deleted.*$/,
    /^.*database is a higher version.*$/,
    /^.*database using a lower version.*$/,
    /^.*disk is full.*$/,
    /^.*Error creating or migrating .* in database.*$/,
    /^.*Error looking up record in object store.*$/,
    /^.*Error:.*Indexed Database server.*$/,
    /^.*Extension context invalidated.*$/,
    /^.*Failed to execute 'replaceState' on 'History'.*$/,
    /^.*flatMap.*$/,
    /^.*Internal error retrieving bucket.*$/,
    /^.*is not defined.*$/,
    /^.*Java object is gone.*$/,
    /^.*Loading CSS chunk.*$/,
    /^.*Munchkin.*$/,
    /^.*Network Error.*$/,
    /^.*NetworkError when attempting to fetch resource.*$/,
    /^.*No Session Id.*$/,
    /^.*no user signed in.*$/,
    /^.*Non-Error promise rejection captured.*$/,
    /^.*One of the specified object stores was not found.*$/,
    /^.*QuotaExceededError.*$/,
    /^.*redefine .*property.*$/,
    /^.*Request aborted.*$/,
    /^.*ResizeObserver loop limit exceeded.*$/,
    /^.*SecurityError: The operation is insecure.*$/,
    /^.*status.*401.*$/,
    /^.*status.*403.*$/,
    /^.*status.*404.*$/,
    /^.*status.*410.*$/,
    /^.*status.*422.*$/,
    /^.*The operation failed for reasons unrelated to the database itself.*$/,
    /^.*The requested version.*$/,
    /^.*The user denied permission to access the database.*$/,
    /^.*Transaction timed out due to inactivity.*$/,
    /^.*Unable to open database.*$/,
    /^.*DataError: Failed to read large IndexedDB value.*$/,
    /^.*window\.onunload is not a function.*$/,
    /^.*is not a child of this node.*$/,
    /^.*Attempt to get a record from database without an in-progress transaction.*$/,
    /^TypeError: Load failed$/,
  ],
  denyUrls: [/edge\.fscdn\.org\/assets\/static\/css\//],
  allowUrls: [
    /edge\.fscdn\.org/,
    /familysearchcdn\.org/,
    /https?:\/\/((www|beta|integration)\.)?familysearch\.org/,
    /consent\.trustarc\.com/,
  ],
}

if (sentryDSN) {
  Sentry.init(
    mergeSentryConfig({
      ...zionSentryDefaults,
      ...treeSentryDefaults,
      dsn: sentryDSN,
    })
  )
}

const base = appPath ? new URL(appPath).pathname : ''

const FrontierRoot = () => (
  <React.StrictMode>
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockMboxes={mockActivities}>
        <Router basename={base}>
          <Root analytics>
            <AppLayout fullWidth>
              <App />
            </AppLayout>
          </Root>
        </Router>
      </FrontierBaseProviders>
    </Suspense>
  </React.StrictMode>
)

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

serviceWorkerRegistration.unregister()
